// =============================================================================
// CharGrid.ts — Grade de seleção de personagem (menu e lobby usam a mesma,
// o lobby em versão compacta). No modo cheio cada card é um "santinho" de
// campanha: número de urna, miniatura animada do ataque (issue #6) e o
// bonequinho andando pra baixo ao lado do nome.
// Personagem com skin (issue #24) ganha um seletor discreto de traje no card.
// Curral Eleitoral (#90): candidato ainda travado aparece como SILHUETA com
// cadeado pixel art e o total de votos vitalícios que falta pra abrir o sigilo.
// =============================================================================
import { CHARACTER_LIST } from '../../../shared/characters';
import { DEFAULT_SKIN, hasSkins, skinsFor } from '../../../shared/skins';
import { charUnlockVotes, isCharUnlocked } from '../../../shared/meta';
import { AttackPreview } from './AttackPreview';
import { charImg, splitNome } from './dom';
import { arrowPixelIcon, cadeadoIcon } from './pixelIcons';

// Quem controla as skins (GameApp): qual está escolhida e o que fazer na troca
export interface SkinHandlers {
  skinOf: (charId: string) => string;
  onSkinChange: (charId: string, skinId: string) => void;
}

export class CharGrid {
  private previews: AttackPreview[] = [];
  // Votos vitalícios do jogador (null = perfil ainda não carregou: trava tudo
  // que tem marco — o servidor valida de novo no início da partida)
  private votos: number | null = null;

  constructor(
    private readonly el: HTMLElement,
    private readonly compact = false,
  ) {}

  // Perfil chegou/mudou: o dono da grade re-renderiza com os cadeados certos
  setVotos(votos: number | null): void {
    this.votos = votos;
  }

  private unlocked(charId: string): boolean {
    return isCharUnlocked(this.votos ?? 0, charId);
  }

  render(selected: string, onSelect: (id: string) => void, skins?: SkinHandlers): void {
    for (const p of this.previews) p.dispose();
    this.previews = [];
    // O re-render destrói os cards; guarda qual tinha foco pra devolvê-lo ao
    // card recriado (senão a troca de traje por teclado perderia o foco).
    const focusedChar = this.el.contains(document.activeElement)
      ? (document.activeElement as HTMLElement).closest<HTMLElement>('.santinho')?.dataset.char
      : undefined;
    this.el.innerHTML = '';
    for (const ch of CHARACTER_LIST) {
      const skinId = skins?.skinOf(ch.id) ?? DEFAULT_SKIN;
      const aberto = this.unlocked(ch.id);
      const faltam = Math.max(0, (charUnlockVotes(ch.id) ?? 0) - (this.votos ?? 0));
      const card = document.createElement('button');
      card.type = 'button';
      card.dataset.char = ch.id;
      card.className = `santinho${this.compact ? ' compacto' : ''}`
        + `${ch.id === selected ? ' sel' : ''}${aberto ? '' : ' sigilo'}`;
      const { nome, titulo } = splitNome(ch.nome);
      const faltamTxt = `faltam ${faltam.toLocaleString('pt-BR')} votos`;
      if (aberto) {
        card.innerHTML = this.compact
          ? `
          <span class="check" aria-hidden="true">✓</span>
          ${charImg(ch.id, 48, true, skinId)}
          <span class="nome">${nome}</span>
          <span class="titulo">${titulo}</span>`
          : `
          <span class="palco"></span>
          <span class="ficha">
            <span class="linha-nome">
              <span class="numero">${ch.numero}</span>
              <span class="nome">${ch.nome}</span>
              ${charImg(ch.id, 36, true, skinId)}
            </span>
            <span class="desc">${ch.desc}</span>
            <span class="voto">✓ VOTO REGISTRADO</span>
          </span>`;
        card.title = ch.fraseSelecao;
        card.onclick = () => onSelect(ch.id);
        // Seletor de traje (issue #24) só em candidato já fora do sigilo
        if (skins && hasSkins(ch.id)) {
          (this.compact ? card : card.querySelector('.ficha')!)
            .appendChild(this.skinSelector(card, ch.id, skinId, skins));
        }
      } else {
        // Silhueta com cadeado: o retrato vira sombra via CSS (.sigilo)
        card.innerHTML = this.compact
          ? `
          <span class="cadeado" aria-hidden="true">${cadeadoIcon(18)}</span>
          ${charImg(ch.id, 48)}
          <span class="nome">SOB SIGILO</span>
          <span class="titulo">${faltamTxt}</span>`
          : `
          <span class="palco"><span class="cadeado-palco">${cadeadoIcon(34)}</span></span>
          <span class="ficha">
            <span class="linha-nome">
              <span class="numero">?</span>
              <span class="nome">CANDIDATO SOB SIGILO</span>
              ${charImg(ch.id, 36)}
            </span>
            <span class="desc">Candidato sob sigilo de 100 anos — ${faltamTxt} vitalícios pra abrir o dossiê.</span>
            <span class="voto">${cadeadoIcon(14)} ${faltamTxt.toUpperCase()}</span>
          </span>`;
        card.title = `Candidato sob sigilo — ${faltamTxt}`;
        card.disabled = true;
      }
      if (!this.compact && aberto) {
        const canvas = document.createElement('canvas');
        canvas.className = 'atk-prev';
        card.querySelector('.palco')!.appendChild(canvas);
        this.previews.push(new AttackPreview(canvas, ch, skinId));
      }
      this.el.appendChild(card);
      if (ch.id === focusedChar) card.focus();
    }
  }

  // Seletor discreto de traje: seta esquerda, nome da skin, seta direita.
  // Spans (não <button>) porque o card inteiro já é um botão e HTML não permite
  // controle interativo aninhado; o clique nas setas não pode "vazar" pra
  // seleção do personagem. Pelo teclado, com o card focado (Tab), as teclas
  // esquerda/direita trocam o traje — as setas visuais são só afordância de
  // mouse/touch, por isso ficam aria-hidden.
  private skinSelector(card: HTMLButtonElement, charId: string, skinId: string, skins: SkinHandlers): HTMLElement {
    const list = skinsFor(charId);
    const idx = Math.max(0, list.findIndex((s) => s.id === skinId));
    const cycle = (delta: number, e: Event): void => {
      e.stopPropagation();
      const next = list[(idx + delta + list.length) % list.length];
      skins.onSkinChange(charId, next.id);
    };
    card.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') cycle(-1, e);
      else if (e.key === 'ArrowRight') cycle(1, e);
      else return;
      e.preventDefault();
    });
    const sel = document.createElement('span');
    sel.className = 'skin-sel';
    sel.title = list[idx].desc;
    const esq = document.createElement('span');
    esq.className = 'skin-seta';
    esq.setAttribute('aria-hidden', 'true');
    esq.innerHTML = arrowPixelIcon('esq');
    esq.onclick = (e) => cycle(-1, e);
    const nome = document.createElement('span');
    nome.className = 'skin-nome';
    nome.textContent = list[idx].nome.toUpperCase();
    const dir = document.createElement('span');
    dir.className = 'skin-seta';
    dir.setAttribute('aria-hidden', 'true');
    dir.innerHTML = arrowPixelIcon('dir');
    dir.onclick = (e) => cycle(1, e);
    sel.append(esq, nome, dir);
    return sel;
  }
}
